import { ApiError, HttpError, type ApiResponse } from "@/infrastructure/http/types";

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
};

async function unwrap<T>(response: Response): Promise<T> {
  let body: ApiResponse<T>;

  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new HttpError("Invalid JSON response", response.status);
  }

  if (!response.ok) {
    throw new HttpError(body?.message || response.statusText || "Request failed", response.status);
  }

  if (body.isError) {
    throw new ApiError(body.message || "API returned an error", body.data);
  }

  return body.data;
}

export class HttpClient {
  constructor(private readonly baseUrl: string) {
    if (!baseUrl) {
      throw new Error("HttpClient requires a non-empty base URL");
    }
  }

  private url(path: string): string {
    const normalized = path.startsWith("/") ? path : `/${path}`;
    return `${this.baseUrl}${normalized}`;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, headers, ...rest } = options;
    const response = await fetch(this.url(path), {
      ...rest,
      headers: {
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    return unwrap<T>(response);
  }

  get<T>(path: string, init?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...init, method: "GET" });
  }

  post<T>(path: string, body?: unknown, init?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...init, method: "POST", body });
  }

  patch<T>(path: string, body?: unknown, init?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...init, method: "PATCH", body });
  }

  delete<T>(path: string, init?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...init, method: "DELETE" });
  }
}
